import { type FC, useMemo } from 'react';
import { graphql, useFragment, useSubscription } from 'react-relay';
import { defineMessages, useIntl } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { Spinner } from 'dibs-elements/exports/Spinner.tsx';
import { ioParams } from 'dibs-fastly/exports/imageOptimizations.ts';

import { useRouteClickHandler } from '../useRouteClickHandler.ts';
import { paths } from '../../../../constants.ts';

import { type NftProfileImportCollectionListTile_contractImport$key } from './__generated__/NftProfileImportCollectionListTile_contractImport.graphql.ts';
import { type NftProfileImportCollectionListTile_publicProfile$key } from './__generated__/NftProfileImportCollectionListTile_publicProfile.graphql.ts';

import styles from './styles/NftProfileImportCollectionList.scss';

const COLLECTION_SIZE = '195';

const IO_PARAMS = ioParams({
    height: COLLECTION_SIZE,
    width: COLLECTION_SIZE,
    dpr: '2',
    precrop: '1:1',
    'resize-filter': 'bilinear',
    format: 'pjpg',
    auto: 'webp',
}).toQuery();

const IMG_EXT_REGEX = /(png|jpg|jpeg)$/;

const messages = defineMessages({
    importing: { id: 'nftProfile.importCollectionListTile.importing', defaultMessage: 'Importing' },
    nftCount: {
        id: 'nftProfile.importSection.nftCount',
        defaultMessage: `{count, plural,
            one {# NFT}
            other {# NFTs}
        }`,
    },
});

const subscription = graphql`
    subscription NftProfileImportCollectionListTileSubscription($id: String!) {
        nftTokenContractImportUpdate(input: { tokenContractId: $id }) {
            nftTokenContractImport {
                ...NftProfileImportCollectionListTile_contractImport
            }
        }
    }
`;
const publicProfileFragment = graphql`
    fragment NftProfileImportCollectionListTile_publicProfile on PublicProfile {
        username
    }
`;
const contractFragment = graphql`
    fragment NftProfileImportCollectionListTile_contractImport on NftTokenContractImport {
        collectionName
        importStatus
        serviceId
        contract {
            name
        }
        items(
            first: 4
            importStatuses: [
                NFT_ITEM_IMPORT_STATUS_COMPLETE
                NFT_ITEM_IMPORT_STATUS_PROCESSING
                NFT_ITEM_IMPORT_STATUS_ERROR
            ]
        ) {
            totalResults
            edges {
                node {
                    serviceId
                    metadata {
                        ... on NftDigitalArtMetadata {
                            media {
                                poster: posterFrameUrl
                                web: webUrl
                            }
                        }
                    }
                }
            }
        }
        completeItems: items(importStatuses: [NFT_ITEM_IMPORT_STATUS_COMPLETE]) {
            totalResults
        }
    }
`;

export const NftProfileImportCollectionListTile: FC<{
    collection: NftProfileImportCollectionListTile_contractImport$key;
    publicProfile: NftProfileImportCollectionListTile_publicProfile$key | null | undefined;
}> = ({ collection: collectionRef, publicProfile: publicProfileRef }) => {
    const { formatMessage } = useIntl();
    const publicProfile = useFragment(publicProfileFragment, publicProfileRef);
    const contractImport = useFragment(contractFragment, collectionRef);
    const id = contractImport.serviceId;
    useSubscription(useMemo(() => ({ variables: { id }, subscription }), [id]));
    const name = contractImport.collectionName || contractImport.contract?.name;
    const count = contractImport.completeItems?.totalResults || 0;
    const handleClick = useRouteClickHandler(contractImport.serviceId);

    let thumbnail = null;
    for (const edge of contractImport.items?.edges || []) {
        const { poster, web } = edge?.node?.metadata?.media || {};
        if (poster) {
            thumbnail = poster + IO_PARAMS;
            break;
        } else if (web && IMG_EXT_REGEX.test(web)) {
            thumbnail = web + IO_PARAMS;
            break;
        }
    }

    const collectionUrl = publicProfile?.username
        ? `${paths.profileUser}${publicProfile.username}/${contractImport.serviceId}/`
        : undefined;

    if (contractImport.importStatus === 'NFT_TOKEN_CONTRACT_IMPORT_STATUS_PROCESSING') {
        return (
            <>
                <li className={styles.tile}>
                    <div className={styles.placeholder}>
                        <Spinner black />
                    </div>
                    <p className={styles.name}>{name}</p>
                    <p className={styles.status}>{formatMessage(messages.importing)}</p>
                </li>
            </>
        );
    }

    return (
        <li className={styles.tile}>
            <Link className={styles.thumbnailLink} onClick={handleClick} href={collectionUrl}>
                {thumbnail ? (
                    <img className={styles.thumbnail} alt="" src={thumbnail} />
                ) : (
                    <div className={styles.placeholder} />
                )}
            </Link>
            <Link
                onClick={handleClick}
                buyerLinkType="linkNoDecoration"
                className={styles.name}
                href={collectionUrl}
            >
                {name}
            </Link>
            <p className={styles.status}>{formatMessage(messages.nftCount, { count: count })}</p>
        </li>
    );
};
